import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { minorUnitsToInput } from '../lib/money-input';

type DateInput = Date | string | number;

export type Formatter = {
  formatDate: (value: DateInput) => string;
  formatDateTime: (value: DateInput) => string;
  /** The time alone, e.g. "12:00 PM". */
  formatTime: (value: DateInput) => string;
  /** The day of the week in full, e.g. "Monday". */
  formatWeekday: (value: DateInput) => string;
  formatNumber: (value: number, options?: Intl.NumberFormatOptions) => string;
  /** `minorUnits` is an integer (cents). Money is never a float anywhere in the app. */
  formatCurrency: (minorUnits: number, currencyCode: string) => string;
  /** Elapsed milliseconds in the two largest units, e.g. "4 min 12 sec", "1 hr 3 min". */
  formatDuration: (milliseconds: number) => string;
  /** A stored file's size: bytes under 1 kB, whole kB under 1 MB, then MB to one decimal. */
  formatFileSize: (bytes: number) => string;
};

/**
 * The one place dates, numbers and money are formatted. Dates are medium style with the month
 * as a word (never all-numeric), currency always shows its code ("USD 725.00"), and times are
 * in the viewer's zone unless `timeZone` is given (tests pin it).
 */
export function createFormatter(
  locale: string,
  { timeZone }: { timeZone?: string } = {},
): Formatter {
  const zone = timeZone ? { timeZone } : {};
  const date = new Intl.DateTimeFormat(locale, { dateStyle: 'medium', ...zone });
  const dateTime = new Intl.DateTimeFormat(locale, {
    dateStyle: 'medium',
    timeStyle: 'short',
    ...zone,
  });
  const time = new Intl.DateTimeFormat(locale, { timeStyle: 'short', ...zone });
  const weekday = new Intl.DateTimeFormat(locale, { weekday: 'long', ...zone });
  return {
    formatDate: (value) => date.format(new Date(value)),
    formatDateTime: (value) => dateTime.format(new Date(value)),
    formatTime: (value) => time.format(new Date(value)),
    formatWeekday: (value) => weekday.format(new Date(value)),
    formatNumber: (value, options) => new Intl.NumberFormat(locale, options).format(value),
    formatCurrency: (minorUnits, currencyCode) => {
      if (!Number.isInteger(minorUnits)) throw new RangeError('money is integer minor units');
      // Intl takes a decimal string exactly, so the amount is never divided into a float.
      const decimal = `${minorUnits < 0 ? '-' : ''}${minorUnitsToInput(Math.abs(minorUnits))}`;
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currencyCode,
        currencyDisplay: 'code',
      }).format(decimal as `${number}`);
    },
    formatDuration: (milliseconds) => {
      const total = Math.max(0, Math.floor(milliseconds / 1000));
      const counts = [
        ['hour', Math.floor(total / 3600)],
        ['minute', Math.floor((total % 3600) / 60)],
        ['second', total % 60],
      ] as const;
      const first = counts.findIndex(([, count]) => count > 0);
      if (first === -1) return unitFormat(locale, 'second').format(0);
      return counts
        .slice(first, first + 2)
        .filter(([, count]) => count > 0)
        .map(([unit, count]) => unitFormat(locale, unit).format(count))
        .join(' ');
    },
    formatFileSize: (bytes) => {
      const size = (unit: 'byte' | 'kilobyte' | 'megabyte', value: number, digits = 0) =>
        new Intl.NumberFormat(locale, {
          style: 'unit',
          unit,
          unitDisplay: unit === 'byte' ? 'long' : 'short',
          maximumFractionDigits: digits,
        }).format(value);
      if (bytes < 1000) return size('byte', bytes);
      if (bytes < 1_000_000) return size('kilobyte', Math.round(bytes / 1000));
      return size('megabyte', bytes / 1_000_000, 1);
    },
  };
}

function unitFormat(locale: string, unit: 'hour' | 'minute' | 'second') {
  return new Intl.NumberFormat(locale, { style: 'unit', unit, unitDisplay: 'short' });
}

/**
 * Formatter bound to the active language. Times are in the viewer's zone; pass `timeZone` where everyone must
 * read the same clock (the staff queue is in Toronto time).
 */
export function useFormat({ timeZone }: { timeZone?: string } = {}): Formatter {
  const { i18n } = useTranslation();
  return useMemo(
    () => createFormatter(i18n.language, timeZone ? { timeZone } : {}),
    [i18n.language, timeZone],
  );
}
